/* eslint-disable react/jsx-filename-extension */
/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { rankImages } from 'config/constants'
import { getTreeNode } from 'state/tree/api'
import { addTreeNode } from 'state/tree/actions'
import { useDispatch } from 'react-redux'
import { formatAmount } from 'utils/formatInfoNumbers'
import { Input } from 'antd'
import debounce from 'lodash/debounce'
import { useTranslation } from 'contexts/Localization'
import { useUserData } from 'state/user/hooks/useUserData'
import {AdminSearchInput}  from 'components'
import styled from 'styled-components'
import { SearchIcon } from '../../../packages/uikit/src/components/Svg'

const height = 800

const hexagonSize = 30
const centerX = 55
const centerY = 55

const points = Array.from({ length: 6 }, (_, number) => number).map((item) => {
  const angle = (Math.PI / 3) * item + Math.PI / 6
  const x = centerX + hexagonSize * Math.cos(angle)
  const y = centerY + hexagonSize * Math.sin(angle)

  return [x, y]
})

const HorizontalTree = ({ data }) => {
  console.log('------------------')
  console.log(data)
  console.log('------------------')
  const svgRef = useRef(null)
  const inputRef = useRef(null)

  const userData = useUserData()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [width, setWidth] = useState(984)

  // Increase the spacing values
  const dx = 100 // Horizontal spacing (doubled from 200)
  const dy = 150 // Vertical spacing (doubled from 300)
  const margin = { top: 10, right: 10, bottom: 10, left: 10 }

  /**
   * function
   * =================================================================
   */

  /**
   * use Effect
   * =================================================================
   */

  useEffect(() => {
    const root = d3.hierarchy(data)

    const tree = d3.tree().nodeSize([dx, dy])
    const diagonal = d3
      .linkVertical()
      .x((d) => d?.x)
      .y((d) => d?.y)

    root.x0 = dx / 2
    root.y0 = 0
    root.descendants()?.forEach((d, i) => {
      d.id = d?.data?.id
      d._children = d.children
    })

    const svg = d3.select('#svg')

    let g
    let gNode
    let gLinks

    const zoom = d3
      .zoom()
      .extent([
        [0, 0],
        [width, height],
      ])
      .scaleExtent([0.1, 8])
      .on('zoom', zoomed)

    if (svg.select('g').empty()) {
      g = svg.append('g').attr('transform', `translate(${width / 2},${margin.top})`)

      gLinks = g
        .append('g')
        .attr('id', 'link')
        .attr('fill', 'none')
        .attr('stroke', '#555')
        .attr('stroke-opacity', 0.4)
        .attr('stroke-width', 1.5)
        .attr('transform', 'translate(0,0)')

      gNode = g.append('g').attr('id', 'node').attr('cursor', 'pointer').attr('pointer-events', 'all')

      svg.call(zoom)
    } else {
      g = svg.select('g')

      gLinks = g.select('#link')

      gNode = g.select('#node')
    }

    const Tooltip = d3.select('#tooltipTree').empty()
      ? d3
          .select('body')
          .append('div')
          .attr('id', 'tooltipTree')
          .attr('class', 'absolute opacity-0 top-0 p-3')
          .style('border-radius', 'var(--spacing-spacing-none, 0px)')
          .style('border', ' 2px solid var(--color-brand-futuristic-alpha-10, rgba(173, 247, 0, 0.10))')
          .style('background', 'var(--surface-elavation-1, rgba(253, 255, 250, 0.05))')
          .style('backdrop-filter', 'blur(30px)')
      : d3.select('#tooltipTree')

    function zoomed({ transform }) {
      g.attr('transform', transform)
    }

    const update = (event, source) => {
      const duration = event && event.altKey ? 2500 : 250
      const nodes = root.descendants().reverse()
      const links = root.links()

      // Compute the new tree layout.
      tree(root)

      const transition = svg
        .transition()
        .duration(duration)
        .tween('resize', window.ResizeObserver ? null : () => () => svg.dispatch('toggle'))

      // Update the nodes…
      const node = gNode.selectAll('g').data(nodes, (d) => d.id)

      // Enter any new nodes at the parent's previous position.
      const nodeEnter = node
        .enter()
        .append('g')
        .attr('transform', (d) => `translate(${source.x0},${source.y0})`)
        .attr('fill-opacity', 0)
        .attr('stroke-opacity', 0)
        .on('mouseenter', mouseenter)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave)
        .on('click', (_event, d) => {
          console.log(d.data)
          if (d._children) {
            d.children = d.children ? null : d._children
            update(event, d)
            if (event && event.altKey) {
              setTimeout(() => {
                zoomToFit()
              }, duration + 100)
              zoomToFit()
            }
            return
          }

          if (!d?.data?.totalF1) {
            return
          }

          getTreeNode(d?.data?.userId?.id).then((res) => {
            if (!res?.length) return

            dispatch(addTreeNode({ nodeId: d?.id, children: res }))

            const temp = res?.map((item) => {
              const _item = d3.hierarchy(item)

              return _item
            })

            const updatedNode = root.descendants().find((di) => di.id === d.id)
            updatedNode.children = temp
            updatedNode._children = updatedNode.children

            // Now, you can access the updated x and y coordinates for each node
            updatedNode.children.forEach((child) => {
              // Ensure that `d` is defined and contains necessary properties
              if (d && d.x !== undefined && d.y !== undefined) {
                // Calculate or set x and y values as needed
                child.x0 = child.x
                child.id = child?.data?.id
                child.y0 = child.y
                child.depth = d.depth + 1 // For example, set depth as you intended
                child.parent = updatedNode
              }
            })

            tree(root)

            update(event, updatedNode)
          })
        })

      nodeEnter
        .append('image')
        .attr('width', 110)
        .attr('xlink:href', (d) => rankImages[d?.data?.rank ?? 0])
        .attr('x', -55)

      const badgeDef = nodeEnter
        .append('svg:defs')
        .append('svg:pattern')
        .attr('id', (d, i) => `avatar_${d?.data?.userId?.id}`)
        .attr('width', 1)
        .attr('height', 1)
        .attr('patternUnits', 'objectBoundingBox')

      badgeDef.append('rect').attr('width', 60).attr('height', 60).attr('fill', 'black')

      badgeDef
        .append('svg:image')
        .attr('xlink:href', (d) => d.data.userId?.avatar)
        .attr('height', 60)
        .attr('width', 60)
        .attr('preserveAspectRatio', 'xMidYMid slice')
        .attr('x', -4)
        .attr('y', 0)

      nodeEnter
        .append('polygon')
        .attr('points', points.map((point) => point.join(',')).join(' '))
        .attr('fill', (d, i) => `url(#avatar_${d?.data?.userId?.id})`)
        .attr('stroke-width', 2)
        .attr('transform', 'translate(-55, 0)')

      nodeEnter
        .append('circle')
        .attr('r', 4)
        .attr('fill', (d) => {
          return d._children || d?.data?.totalF1 ? '#ADF700' : '#fff'
        })
        .attr('stroke-width', 10)
        .attr('transform', (d) => `translate(0,0)`)

      nodeEnter
        .append('foreignObject')
        .attr('width', 160)
        .attr('height', 130)
        .attr('x', -76)
        .attr('y', 0)
        .attr('class', 'chart-item')
        .append('xhtml:body')
        .html((d) => {
          return `<div style="background-color:rgb(0 0 0 / 0%) !important" class="w-full h-full p-3 flex justify-end flex-col items-center gap-2">
        
        <p class="text-center text-sm truncate w-full"></p>
      </div>`
        })

      // Transition nodes to their new position.
      const nodeUpdate = node
        .merge(nodeEnter)
        .transition(transition)
        .attr('transform', (d) => {
          return `translate(${d.x},${d.y})`
        })
        .attr('fill-opacity', 1)
        .attr('stroke-opacity', 1)

      nodeUpdate.select('circle').attr('fill', (d) => {
        return d._children || d?.data?.totalF1 ? '#ADF700' : '#fff'
      })

      // Transition exiting nodes to the parent's new position.
      const nodeExit = node
        .exit()
        .transition(transition)
        .remove()
        .attr('transform', (d) => {
          return `translate(${source.x},${source.y})`
        })
        .attr('fill-opacity', 0)
        .attr('stroke-opacity', 0)

      // Update the links…
      const link = gLinks.selectAll('path').data(links, (d) => d.target.id)

      // Enter any new links at the parent's previous position.
      const linkEnter = link
        .enter()
        .append('path')
        .attr('d', (d) => {
          const o = { x: source.x0, y: source.y0 }
          return diagonal({ source: o, target: o })
        })

      // Transition links to their new position.
      link
        .merge(linkEnter)
        .transition(transition)
        .attr('d', (d) => {
          return diagonal({
            source: { x: d?.source?.x, y: d?.source?.y + 130 },
            target: { x: d?.target?.x, y: d?.target?.y },
          })
        })

      // Transition exiting nodes to the parent's new position.
      link
        .exit()
        .transition(transition)
        .remove()
        .attr('d', (d) => {
          const o = { x: source.x, y: source.y }
          return diagonal({ source: o, target: o })
        })

      // Stash the old positions for transition.
      root.eachBefore((d) => {
        d.x0 = d.x
        d.y0 = d.y
      })
    }

    function mouseenter(event, d) {
      Tooltip.html(
        `<div class="flex items-center gap-2">
          ${userData?.role === 'admin' ? `<p>Email: ${d?.data?.userId?.email}</p>` : ""}
        </div>
        <div class="flex items-center gap-2">
          <p>UID: ${d?.data?.userId?.id || d?.data?.userId?.fullName || "No Name"}</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>Level: ${d?.data?.userId?.level || 0}</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>Net Volume: ${d?.data?.totalNetVolume || 0} USDT</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>Net Volume MBC: ${d?.data?.totalNetVolumeMBC || 0} MBC</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>Package: ${d?.data?.totalNodeVolume || 0} USDT</p></br>
        </div>
         <div class="flex items-center gap-2">
          <p>Package MBC: ${d?.data?.totalNodeVolumeMBC || 0} MBC</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>Parent pair Balance: ${d?.data?.totalNodeBalance || 0} USDT</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>Parent pair MBC Balance: ${d?.data?.totalNodeBalanceMBC || 0} USDT (MBC)</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>F1 pair Balance: ${d?.data?.childsPairBalance || 0} USDT</p></br>
        </div>
        <div class="flex items-center gap-2">
          <p>F1 pair MBC Balance: ${d?.data?.childsPairBalanceMBC || 0} USDT (MBC)</p></br>
        </div>`,
      ).style('opacity', 1).style('z-index', 9999)
    }

    const mousemove = function (event, d) {
      Tooltip.style('left', `${event?.pageX + 10}px`).style('top', `${event?.pageY}px`)
    }
    const mouseleave = function (event, d) {
      Tooltip.style('opacity', 0)
    }

    const zoomToFit = (paddingPercent = 0.75) => {
      const bounds = g.node().getBBox()
      const parent = svg.node().parentElement
      const fullWidth = parent.clientWidth
      const fullHeight = parent.clientHeight

      const { width: zoomWidth } = bounds
      const { height: zoomHeight } = bounds

      const midX = bounds.x + zoomWidth / 2
      const midY = bounds.y + zoomHeight / 2

      if (zoomWidth === 0 || zoomHeight === 0) return // nothing to fit

      const scale = paddingPercent / Math.max(zoomWidth / fullWidth, zoomHeight / fullHeight)
      const translate = [fullWidth / 2 - scale * midX, fullHeight / 2 - scale * midY]

      const transform = d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)

      svg.transition().duration(500).call(d3.zoomBehaviours.transform, transform)
    }

    update(null, root)

    const handleInput = debounce(() => {
      // This function will be executed whenever the input value changes.
      const inputValue = inputRef?.current?.input?.value

      const regex = new RegExp(inputValue, 'i')

      const foundNode = root.descendants().find((di) => regex.test(di.data?.userId?.fullName))

      if (!foundNode) return

      const zoomTransform = d3.zoomIdentity
        .translate(width / 2 - foundNode.x, height / 2 - foundNode.y) // Adjust as needed
        .scale(1) // Adjust the scale level as needed

      svg
        .transition()
        .duration(500) // Adjust the duration as needed
        .call(zoom.transform, zoomTransform)
    }, 500)

    inputRef?.current?.input.addEventListener('input', handleInput)
  }, [width])

  const TreeContainer = styled.div`
    background-color: gray;
    width: 100%;
    height: 100%;
    position: relative;
  `;

  return (
    <TreeContainer ref={(el) => setWidth(el?.clientWidth)} className="w-full h-full relative">
      {userData?.role === 'admin' ? <AdminSearchInput /> : <Input ref={inputRef} className="mb-5 max-w-sm" placeholder={t('Find name')} suffix={<SearchIcon />} />}
      <svg ref={svgRef} id="svg" width={width} height={height} />
    </TreeContainer>
  )
}

export default HorizontalTree
